import React, { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import API from "../config/config";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import SortableTh from "../components/SortableTh";
import CancellationTimingsModal from "../components/CancellationTimingsModal";
import RideDetailModal from "../components/RideDetailModal";

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadgeClass = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "suspended":
      return "bg-yellow-100 text-yellow-800";
    case "banned":
      return "bg-red-100 text-red-800";
    case "active":
      return "bg-green-100 text-green-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const CancellationSuspendedDrivers = () => {
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 20,
    hasNext: false,
    hasPrev: false,
  });

  const [timingsModal, setTimingsModal] = useState({
    show: false,
    driver: null,
    cancellations: [],
    loading: false,
  });

  const [detailModal, setDetailModal] = useState({ show: false, ride: null });
  const [rideDetailLoading, setRideDetailLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortConfigRef = useRef({ key: null, direction: "asc" });

  const sortThClass =
    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hover:bg-gray-100";

  const fetchDrivers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
      });
      if (search.trim()) params.append("search", search.trim());
      if (sortConfigRef.current?.key) {
        params.append("sortBy", sortConfigRef.current.key);
        params.append("sortOrder", sortConfigRef.current.direction || "desc");
      }

      const response = await axiosInstance.get(
        `${API.ADMIN.CANCELLATION_SUSPENDED_DRIVERS}?${params}`
      );

      if (response.data?.success) {
        setDrivers(response.data.data.drivers || []);
        setPagination(response.data.data.pagination || pagination);
      }
    } catch (error) {
      console.error("Error fetching suspended drivers:", error);
      toast.error("Failed to load suspended drivers");
    } finally {
      setLoading(false);
    }
  }, [search, pagination.limit]);

  useEffect(() => {
    fetchDrivers(1);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDrivers(1);
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    sortConfigRef.current = next;
    fetchDrivers(1);
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      sortConfigRef.current = next;
      fetchDrivers(1);
      return next;
    });
  };

  const sortTh = (columnKey, label) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={sortThClass}
      theme="green"
    />
  );

  const openTimingsModal = async (driver) => {
    setTimingsModal({
      show: true,
      driver,
      cancellations: [],
      loading: true,
    });

    try {
      const response = await axiosInstance.get(
        API.ADMIN.CANCELLATION_SUSPENDED_DRIVER_CANCELLATIONS(driver.driverId)
      );
      if (response.data?.success) {
        setTimingsModal((prev) => ({
          ...prev,
          cancellations: response.data.data.cancellations || [],
          loading: false,
        }));
      } else {
        setTimingsModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      toast.error("Failed to load cancellation timings");
      setTimingsModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const openRideDetail = async (rideId) => {
    if (!rideId) return;
    setRideDetailLoading(true);
    try {
      const response = await axiosInstance.get(`${API.ADMIN.RIDE_DETAIL}/${rideId}`);
      if (response.data?.success) {
        setDetailModal({
          show: true,
          ride: response.data.data.ride,
        });
      } else {
        toast.error("Ride not found");
      }
    } catch (error) {
      console.error("Error fetching ride details:", error);
      toast.error("Failed to load ride details");
    } finally {
      setRideDetailLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cancelation & Suspended — Driver</h1>
        <p className="text-sm text-gray-600 mt-1">
          Drivers with suspended accounts and their cancellation history.
        </p>
      </div>

      <form onSubmit={handleSearch} className="mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search by name or mobile..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 min-w-[240px] flex-1 max-w-md"
        />
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Search
        </button>
      </form>

      {loading && <Loader />}

      {!loading && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {sortTh("driverName", "Driver name")}
                  {sortTh("mobileNumber", "Mobile number")}
                  {sortTh("cancellationCount", "Cancellation timings")}
                  {sortTh("suspensionStarts", "Suspension Starts")}
                  {sortTh("suspensionEnd", "Suspension end")}
                  {sortTh("status", "Status")}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {drivers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No suspended drivers found.
                    </td>
                  </tr>
                ) : (
                  drivers.map((row) => (
                    <tr key={row.driverId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {row.driverName}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {row.mobileNumber}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span>{row.cancellationCount} cancellation(s)</span>
                        {" · "}
                        <button
                          type="button"
                          onClick={() => openTimingsModal(row)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        >
                          View more
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {formatDateTime(row.suspensionStarts)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {formatDateTime(row.suspensionEnd)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${statusBadgeClass(
                            row.status
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.total}{" "}
                total)
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!pagination.hasPrev}
                  onClick={() => fetchDrivers(pagination.currentPage - 1)}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={!pagination.hasNext}
                  onClick={() => fetchDrivers(pagination.currentPage + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {rideDetailLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30">
          <Loader />
        </div>
      )}

      <CancellationTimingsModal
        show={timingsModal.show}
        subject={
          timingsModal.driver
            ? {
                name: timingsModal.driver.driverName,
                mobile: timingsModal.driver.mobileNumber,
              }
            : null
        }
        cancellations={timingsModal.cancellations}
        loading={timingsModal.loading}
        emptyMessage="No driver-cancelled rides found."
        onClose={() =>
          setTimingsModal({ show: false, driver: null, cancellations: [], loading: false })
        }
        onRideClick={openRideDetail}
      />

      <RideDetailModal
        show={detailModal.show}
        ride={detailModal.ride}
        onClose={() => setDetailModal({ show: false, ride: null })}
      />
    </div>
  );
};

export default CancellationSuspendedDrivers;
